// ZATCA Compliance Dashboard — single screen for e-invoicing health.
//
// Backend: GET /api/zatca/compliance/dashboard (window_days = 7..365).
// Architecture rule: NO submission logic lives here. This page is a
// renderer that asks for retries / opens the invoice page where the
// existing ZATCA submit flow is.
//
// Sections:
//   1. KPI Strip — acceptance %, total, rejected, error, pending.
//   2. Compliance Score donut + grade + components.
//   3. 90-day stacked area trend (accepted vs rejected vs error).
//   4. Top error messages (rolled up).
//   5. Failed submissions table with deep links to /invoices?focus=…
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis,
  CartesianGrid, Legend,
} from 'recharts';
import {
  ShieldCheck, ShieldAlert, ShieldX, TrendingUp, AlertTriangle,
  Loader2, RefreshCw, ExternalLink, CheckCircle2, XCircle,
  Clock, Building2, FileWarning, BarChart3,
} from 'lucide-react';

const GRADE_TONES = {
  excellent: { ring: 'ring-emerald-300', text: 'text-emerald-700', bg: 'bg-emerald-50',  dot: 'bg-emerald-500', label: 'Excellent' },
  healthy:   { ring: 'ring-emerald-200', text: 'text-emerald-600', bg: 'bg-emerald-50',  dot: 'bg-emerald-500', label: 'Healthy' },
  watch:     { ring: 'ring-amber-200',   text: 'text-amber-700',   bg: 'bg-amber-50',    dot: 'bg-amber-500',   label: 'Watch' },
  weak:      { ring: 'ring-orange-200',  text: 'text-orange-700',  bg: 'bg-orange-50',   dot: 'bg-orange-500',  label: 'Weak' },
  critical:  { ring: 'ring-rose-300',    text: 'text-rose-700',    bg: 'bg-rose-50',     dot: 'bg-rose-500',    label: 'Critical' },
  unknown:   { ring: 'ring-slate-200',   text: 'text-slate-600',   bg: 'bg-slate-50',    dot: 'bg-slate-400',   label: 'No data' },
};

const STATUS_BADGE = {
  accepted:  { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Accepted' },
  rejected:  { color: 'bg-rose-100    text-rose-800    border-rose-200',    label: 'Rejected' },
  error:     { color: 'bg-orange-100  text-orange-800  border-orange-200',  label: 'Error' },
  submitted: { color: 'bg-sky-100     text-sky-800     border-sky-200',     label: 'Pending' },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const fmtSAR = (v) => {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`;
};


export default function ZATCACompliance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axiosInstance
      .get(`/zatca/compliance/dashboard?window_days=${windowDays}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        setError(err);
        toast.error('Failed to load compliance dashboard');
      })
      .finally(() => setLoading(false));
  }, [windowDays]);

  useEffect(() => {
    const h = setTimeout(load, 0);
    return () => clearTimeout(h);
  }, [load]);

  if (loading && !data) {
    return (
      <div data-testid="zatca-compliance-loading" className="p-8 flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Building compliance snapshot…
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-8 text-rose-700 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" /> Unable to load compliance dashboard.
      </div>
    );
  }
  if (!data) return null;

  const kpis = data.kpis || {};
  const score = data.compliance_score || {};
  const tone = GRADE_TONES[score.grade] || GRADE_TONES.unknown;

  return (
    <div
      data-testid="zatca-compliance-page"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6 space-y-5"
      dir="ltr"
    >
      <Header
        asOf={data.as_of}
        windowDays={windowDays}
        setWindowDays={setWindowDays}
        onRefresh={load}
        loading={loading}
      />

      {/* KPI strip + Score */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <KPICard
            icon={CheckCircle2}
            label="Acceptance rate"
            value={kpis.acceptance_rate_pct === null ? '—' : `${kpis.acceptance_rate_pct}%`}
            sub={data.compare_90d?.acceptance_rate_pct !== null
              ? `90d: ${data.compare_90d.acceptance_rate_pct}%`
              : ''}
            tone={kpis.acceptance_rate_pct === null ? 'neutral'
              : kpis.acceptance_rate_pct >= 95 ? 'positive'
              : kpis.acceptance_rate_pct >= 75 ? 'warning' : 'danger'}
            testid="kpi-acceptance"
          />
          <KPICard
            icon={BarChart3}
            label={`Submissions (${windowDays}d)`}
            value={`${kpis.total_submissions || 0}`}
            sub={`${data.compare_90d?.total_submissions || 0} in 90d`}
            tone="neutral"
            testid="kpi-total"
          />
          <KPICard
            icon={XCircle}
            label="Rejected"
            value={`${kpis.rejected || 0}`}
            sub={kpis.error_rate_pct > 0 ? `${kpis.error_rate_pct}% error rate` : 'No rejections'}
            tone={(kpis.rejected || 0) > 0 ? 'danger' : 'positive'}
            testid="kpi-rejected"
          />
          <KPICard
            icon={AlertTriangle}
            label="Errors"
            value={`${kpis.error || 0}`}
            sub={(kpis.error || 0) > 0 ? 'Submission transport errors' : 'No transport errors'}
            tone={(kpis.error || 0) > 0 ? 'warning' : 'positive'}
            testid="kpi-errors"
          />
          <KPICard
            icon={Clock}
            label="Pending"
            value={`${kpis.pending || 0}`}
            sub="Submitted, awaiting status"
            tone="neutral"
            testid="kpi-pending"
          />
          <KPICard
            icon={Building2}
            label="Last submission"
            value={kpis.last_submission_at ? fmtDate(kpis.last_submission_at) : '—'}
            sub={kpis.last_submission_at ? fmtTime(kpis.last_submission_at) : 'No activity in window'}
            tone="neutral"
            testid="kpi-last"
          />
        </div>

        <ComplianceScoreCard score={score} tone={tone} />
      </div>

      {/* Trend chart */}
      <TrendCard trend={data.trend} />

      {/* Bottom row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <TopErrorsCard errors={data.top_errors} />
        <ByEnvironmentCard byEnv={data.by_environment} />
      </div>

      {/* Failed invoices table */}
      <FailedInvoicesTable rows={data.failed_invoices} />
    </div>
  );
}


// ─── Header ──────────────────────────────────────────────────────────────


function Header({ asOf, windowDays, setWindowDays, onRefresh, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-sm">
            <ShieldCheck size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="page-title">
              ZATCA Compliance Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Real-time e-invoicing acceptance, error analytics and a clearance-grade compliance score.
              As of {asOf}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            data-testid="window-selector"
            className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-0.5"
          >
            {[7, 30, 90, 180].map((d) => (
              <button
                key={d}
                data-testid={`window-${d}`}
                onClick={() => setWindowDays(d)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                  windowDays === d
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-white'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            data-testid="refresh-btn"
            className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:shadow-sm transition disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── KPI cell ────────────────────────────────────────────────────────────


function KPICard({ icon: Icon, label, value, sub, tone = 'neutral', testid }) {
  const toneClass = {
    positive: 'text-emerald-700',
    neutral:  'text-slate-900',
    warning:  'text-amber-700',
    danger:   'text-rose-700',
  }[tone];
  const iconBg = {
    positive: 'bg-emerald-50 text-emerald-600',
    neutral:  'bg-slate-100  text-slate-500',
    warning:  'bg-amber-50   text-amber-600',
    danger:   'bg-rose-50    text-rose-600',
  }[tone];
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1.5">
        <span className={`p-1 rounded ${iconBg}`}>
          <Icon size={13} />
        </span>
        {label}
      </div>
      <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}


// ─── Compliance Score ────────────────────────────────────────────────────


function ComplianceScoreCard({ score, tone }) {
  const value = score.score;
  const grade = score.grade || 'unknown';
  const cmp = score.components || {};
  // Donut: percentage of the way around the circle the score represents.
  const pct = value !== null && value !== undefined ? Math.max(0, Math.min(100, value)) : 0;
  const dash = `${pct * 2.51}, 251`;  // 2*pi*r where r=40 → ~251.3

  return (
    <div
      data-testid="compliance-score-card"
      className={`bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex flex-col items-center justify-center ring-1 ${tone.ring}`}
    >
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
        Compliance Score
      </div>
      <div className="relative w-40 h-40">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="8" />
          <circle
            cx="50" cy="50" r="40" fill="none"
            stroke={tone.dot === 'bg-emerald-500' ? '#10b981'
              : tone.dot === 'bg-amber-500' ? '#f59e0b'
              : tone.dot === 'bg-orange-500' ? '#f97316'
              : tone.dot === 'bg-rose-500' ? '#e11d48'
              : '#94a3b8'}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={dash}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={`text-4xl font-bold ${tone.text}`}>
            {value !== null && value !== undefined ? value : '—'}
          </div>
          <div className="text-xs font-semibold text-slate-500 mt-1 uppercase">{tone.label}</div>
        </div>
      </div>
      {cmp && Object.keys(cmp).length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2 w-full text-center">
          <ScoreComponent label="Acceptance" value={cmp.acceptance} />
          <ScoreComponent label="Cadence" value={cmp.cadence} />
          <ScoreComponent label="Error decay" value={cmp.error_decay} />
        </div>
      )}
    </div>
  );
}


function ScoreComponent({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2">
      <div className="text-xs text-slate-400 truncate">{label}</div>
      <div className="text-sm font-semibold text-slate-700">{value !== undefined ? `${value}` : '—'}</div>
    </div>
  );
}


// ─── Trend chart ─────────────────────────────────────────────────────────


function TrendCard({ trend }) {
  const hasData = useMemo(() => (trend || []).some((d) => (d.accepted + d.rejected + d.error + d.submitted) > 0), [trend]);
  return (
    <div
      data-testid="trend-card"
      className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
    >
      <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center gap-2">
        <TrendingUp size={16} className="text-indigo-600" />
        <h2 className="text-sm font-semibold text-slate-900">90-day submissions trend</h2>
      </div>
      {hasData ? (
        <div className="p-3" style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="zg-accepted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="zg-rejected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e11d48" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#e11d48" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="zg-error" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f97316" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDate}
                tick={{ fontSize: 11, fill: '#64748b' }}
                interval={Math.max(6, Math.floor((trend?.length || 1) / 8))}
              />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} width={36} />
              <Tooltip
                contentStyle={{
                  fontSize: 12, padding: '8px 10px', border: 'none',
                  borderRadius: 8, background: 'rgba(15,23,42,0.95)', color: 'white',
                }}
                labelFormatter={(d) => fmtDate(d)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area stackId="a" type="monotone" dataKey="accepted" stroke="#10b981" fill="url(#zg-accepted)" isAnimationActive={false} />
              <Area stackId="a" type="monotone" dataKey="rejected" stroke="#e11d48" fill="url(#zg-rejected)" isAnimationActive={false} />
              <Area stackId="a" type="monotone" dataKey="error"    stroke="#f97316" fill="url(#zg-error)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="trend-empty">
          No submission activity in the last 90 days.
        </div>
      )}
    </div>
  );
}


// ─── Top errors ──────────────────────────────────────────────────────────


function TopErrorsCard({ errors }) {
  const items = errors || [];
  const max = items.length ? items[0].count : 1;
  return (
    <div data-testid="top-errors-card" className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center gap-2">
        <FileWarning size={16} className="text-rose-600" />
        <h2 className="text-sm font-semibold text-slate-900">Top error messages</h2>
      </div>
      {items.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400" data-testid="top-errors-empty">
          No errors in the active window.
        </div>
      ) : (
        <ul className="p-5 space-y-3">
          {items.map((e, idx) => (
            <li key={idx} className="text-sm">
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-slate-700 truncate flex-1 mr-3" title={e.message}>{e.message}</span>
                <span className="text-rose-700 font-semibold">×{e.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-rose-50 overflow-hidden">
                <div
                  className="h-full bg-rose-400"
                  style={{ width: `${Math.max(8, (e.count / max) * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ─── By environment ──────────────────────────────────────────────────────


function ByEnvironmentCard({ byEnv }) {
  const entries = Object.entries(byEnv || {});
  return (
    <div data-testid="by-environment-card" className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center gap-2">
        <Building2 size={16} className="text-indigo-600" />
        <h2 className="text-sm font-semibold text-slate-900">Submissions by environment</h2>
      </div>
      {entries.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No environment data in window.</div>
      ) : (
        <ul className="p-5 space-y-2 text-sm">
          {entries.map(([env, count]) => (
            <li key={env} className="flex items-center justify-between">
              <span className="capitalize text-slate-700">{env}</span>
              <span className="font-semibold text-slate-900">{count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ─── Failed invoices ─────────────────────────────────────────────────────


function FailedInvoicesTable({ rows }) {
  const items = rows || [];
  return (
    <div data-testid="failed-invoices-card" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-rose-600" />
          <h2 className="text-sm font-semibold text-slate-900">Failed submissions</h2>
          <span className="text-xs text-slate-400">{items.length}</span>
        </div>
        <span className="text-xs text-slate-500">Open the invoice to retry submission</span>
      </div>
      {items.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="failed-empty">
          No failed submissions in the active window.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Invoice</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">First error</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Submitted</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.error;
                return (
                  <tr
                    key={r.submission_id}
                    data-testid={`failed-row-${r.submission_id}`}
                    className="border-t border-slate-100 hover:bg-rose-50/30 transition"
                  >
                    <td className="px-3 py-2 font-semibold text-slate-900">{r.invoice_number || r.invoice_id || '—'}</td>
                    <td className="px-3 py-2 text-slate-700 truncate max-w-[160px]" title={r.client_name}>{r.client_name || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.color}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-rose-700 truncate max-w-[260px]" title={r.first_error}>
                      {r.first_error || '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{fmtSAR(r.invoice_total)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmtTime(r.submitted_at)}</td>
                    <td className="px-3 py-2">
                      {r.invoice_id && (
                        <a
                          href={`/invoices?focus=${encodeURIComponent(r.invoice_id)}`}
                          data-testid={`failed-open-${r.submission_id}`}
                          className="inline-flex items-center gap-0.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Open <ExternalLink size={11} />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
